// SPDX-License-Identifier: GPL-3.0-or-later
// The view model of the "Featured turns" dialog. It is a pure function of the server answers.
// The page binds the result to the dialog nodes with `textContent`.
// Text rules: short sentences, no urgency, no shame, no rank. Free play is never blocked.

import type { MeResponse, QueueEntryPublic, QueueSnapshot } from "../src/shared/contracts.ts";

/** What the dialog knows. */
export interface PanelInput {
  /** "unknown" before the first answer; "offline" when the API did not answer. */
  readonly api: "unknown" | "offline" | "ready";
  readonly me: MeResponse | null;
  readonly queue: QueueSnapshot | null;
  /** True when no game runs (start screen or game over). A featured turn starts only then. */
  readonly gameIdle: boolean;
  /** The state of the featured stream of this page. */
  readonly stream: "none" | "connecting" | "live" | "lost";
  /** True after the first click on "Forget this device", while the player confirms. */
  readonly confirmForget?: boolean;
}

/** What the dialog shows. */
export interface PanelView {
  readonly status: string;
  readonly credit: string;
  readonly expiry: string;
  readonly canSignIn: boolean;
  readonly canSignOut: boolean;
  readonly signOutLabel: string;
  /** The button that cancels "Forget this device". It shows only while the player confirms. */
  readonly canKeep: boolean;
  readonly canClaim: boolean;
  readonly canJoin: boolean;
  readonly canLeave: boolean;
  readonly canStart: boolean;
  readonly canReconnect: boolean;
  readonly hint: string;
  readonly queueTitle: string;
  readonly queueLines: readonly string[];
}

const EMPTY: Omit<PanelView, "status"> = {
  credit: "",
  expiry: "",
  canSignIn: false,
  canSignOut: false,
  signOutLabel: "Sign out",
  canKeep: false,
  canClaim: false,
  canJoin: false,
  canLeave: false,
  canStart: false,
  canReconnect: false,
  hint: "",
  queueTitle: "Waiting list",
  queueLines: [],
};

/** The label of the confirm button of "Forget this device". */
export const FORGET_CONFIRM_LABEL = "Yes, forget this device";

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/** The page path of the claim dialog. The Worker serves the game page there. */
export const CLAIM_PAGE_PATH = "/claim";

/**
 * True when the page location asks for the claim dialog: the `/claim` path or the `#claim`
 * fragment. A trailing slash is fine.
 */
export function wantsClaimDialog(loc: {
  readonly pathname: string;
  readonly hash: string;
}): boolean {
  return (
    loc.pathname === CLAIM_PAGE_PATH ||
    loc.pathname === `${CLAIM_PAGE_PATH}/` ||
    loc.hash === "#claim"
  );
}

/** The first ten characters of an ISO time are the date. Anything else gives an empty string. */
export function isoDate(iso: string | null): string {
  return iso !== null && /^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10) : "";
}

/** One line for each queue entry. Position 0 is the live turn (decision D3). */
export function queueLines(queue: QueueSnapshot | null): readonly string[] {
  if (queue === null) return [];
  return queue.entries.map(
    (e) => `${e.position === 0 ? "NOW" : e.position}. ${e.display_name} (${e.state})`,
  );
}

/**
 * The text that names what "Forget this device" deletes, or null when it deletes nothing.
 * A device with no Twitch sign-in keeps its paid turns and its place in the waiting list only in
 * the session of this browser. The claim code is used up, so the loss cannot be undone.
 */
export function forgetWarning(me: MeResponse | null): string | null {
  if (me === null || me.signed_in) return null;
  const hasTurns = me.turns_available > 0;
  const hasPlace = me.entry !== null;
  if (!hasTurns && !hasPlace) return null;
  const turns = `${me.turns_available} featured ${plural(me.turns_available, "turn", "turns")}`;
  const place = "your place in the waiting list";
  const what = hasTurns && hasPlace ? `${turns} and ${place}` : hasTurns ? turns : place;
  return `Forgetting this device deletes ${what}. This cannot be undone.`;
}

/** A confirm click that comes this soon after the question is ignored: a double tap must not confirm. */
export const CONFIRM_GUARD_MS = 500;

/** What a click on the sign out button does: ask first, wait, or sign out now. */
export type SignOutStep = "ask" | "wait" | "forget";

/**
 * The first click asks when the device holds something. The click after that signs out, unless it
 * follows the question within CONFIRM_GUARD_MS. `askedAt` is the time of the question, or null.
 */
export function signOutStep(
  me: MeResponse | null,
  askedAt: number | null,
  nowMs: number,
): SignOutStep {
  if (forgetWarning(me) === null) return "forget";
  if (askedAt === null) return "ask";
  return nowMs - askedAt < CONFIRM_GUARD_MS ? "wait" : "forget";
}

function queueTitleOf(queue: QueueSnapshot | null): string {
  if (queue === null) return "Waiting list";
  const count = `${queue.length} ${plural(queue.length, "turn", "turns")}`;
  return `Waiting list: ${count}${queue.paused ? " (paused)" : ""}`;
}

type View = Omit<PanelView, "status">;

/** The view of a player with a session (Twitch sign-in, claimed turns or a queue entry). */
function sessionBase(me: MeResponse, shell: Pick<View, "queueTitle" | "queueLines">, ask: boolean) {
  const expiryDate = isoDate(me.next_expiry);
  const expiry =
    me.turns_available > 0 && expiryDate !== ""
      ? `Your oldest featured turn is kept until ${expiryDate}.`
      : "";
  const hasSession = me.signed_in || me.turns_available > 0 || me.entry !== null;
  const forgetting = ask && forgetWarning(me) !== null;
  const forgetLabel = forgetting ? FORGET_CONFIRM_LABEL : "Forget this device";
  return {
    ...EMPTY,
    ...shell,
    expiry,
    canSignIn: !me.signed_in,
    canSignOut: hasSession,
    signOutLabel: me.signed_in ? "Sign out" : forgetLabel,
    canKeep: forgetting,
    canClaim: true,
  };
}

type Base = ReturnType<typeof sessionBase>;

/** The view while a stream connects, runs or is lost. Other stream states give null. */
function streamView(stream: PanelInput["stream"], base: Base, who: string): PanelView | null {
  switch (stream) {
    case "connecting":
      return {
        ...base,
        status: `${who}Connecting your featured turn.`,
        canClaim: false,
        canSignIn: false,
      };
    case "live":
      return {
        ...base,
        status: `${who}Your featured turn is live on stream.`,
        credit: "",
        canClaim: false,
        canSignIn: false,
        canSignOut: false,
        canKeep: false,
      };
    case "lost":
      return {
        ...base,
        status: `${who}The connection to the stream closed.`,
        hint: "You can reconnect within 30 seconds. Your game goes on as free play.",
        canReconnect: true,
        canClaim: false,
        canSignIn: false,
      };
    default:
      return null;
  }
}

/** The view of a ready turn. A turn starts only when no game runs. */
function readyView(base: Base, who: string, gameIdle: boolean): PanelView {
  return {
    ...base,
    status: `${who}Your featured turn is ready.`,
    hint: gameIdle
      ? "Start it within 2 minutes. If you do not, the turn goes back to your credit."
      : "Finish your free game first. Then start your turn within 2 minutes.",
    canStart: gameIdle,
    canLeave: true,
  };
}

/** The view of a queue entry. Entries that are done or expired give null. */
function entryView(
  entry: QueueEntryPublic | null,
  base: Base,
  who: string,
  gameIdle: boolean,
): PanelView | null {
  switch (entry?.state) {
    case "queued":
      return {
        ...base,
        status: `${who}You are in the waiting list. Your place: ${entry.position}.`,
        canLeave: true,
      };
    case "ready":
      return readyView(base, who, gameIdle);
    case "live":
      return {
        ...base,
        status: `${who}Your featured turn is live.`,
        hint: gameIdle ? "Reconnect to go on." : "",
        canReconnect: gameIdle,
      };
    default:
      return null;
  }
}

/** The view of a player with no queue entry: credit to join with, or none. */
function creditView(me: MeResponse, base: Base, who: string): PanelView {
  if (me.turns_available > 0) {
    return {
      ...base,
      status: `${who}You have ${me.turns_available} featured ${plural(me.turns_available, "turn", "turns")}.`,
      credit: "Join the waiting list to play one on stream.",
      canJoin: true,
    };
  }
  return {
    ...base,
    status: "Free play is open to everyone. A featured turn shows your game on stream.",
    credit: me.signed_in
      ? "You have no featured turns now."
      : "Sign in with Twitch, or enter a claim code.",
  };
}

/** Build the view. */
export function buildPanelView(input: PanelInput): PanelView {
  const shell = { queueTitle: queueTitleOf(input.queue), queueLines: queueLines(input.queue) };
  if (input.api === "offline") {
    return {
      ...EMPTY,
      ...shell,
      status: "Featured turns are not available right now. Free play is not affected.",
    };
  }
  const me = input.me;
  if (input.api === "unknown" || me === null) return { ...EMPTY, ...shell, status: "Loading." };
  const base = sessionBase(me, shell, input.confirmForget === true);
  const who = me.display_name !== null ? `${me.display_name}: ` : "";
  return (
    streamView(input.stream, base, who) ??
    entryView(me.entry, base, who, input.gameIdle) ??
    creditView(me, base, who)
  );
}
